import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import fileDownload from 'react-file-download';

import { Alert, Button } from '@mui/material';

import { useQueueCustomLincPayableReport } from '~/hooks/apis/reporting/useReportExports';

import { formatDateISO, startOfMonth, subMonths } from '~/services/TimeService';

import ErrorDisplay from '../ErrorDisplay';

const formSchema: RJSFSchema = {
  description: 'Please enter your experience dates to export the CustomLinc (SSC) payable report',
  type: 'object',
  properties: {
    startAt: {
      title: 'Select Start Date',
      type: 'string',
      format: 'date',
    },
    endAt: {
      title: 'Select End Date',
      type: 'string',
      format: 'date',
    },
    format: {
      title: 'File Format',
      type: 'string',
      enum: ['csv', 'email'],
    },
  },
};

const initialFormData = {
  startAt: formatDateISO(startOfMonth(subMonths(1))),
  endAt: formatDateISO(startOfMonth()),
  format: 'email' as const,
};

const uiSchema = {
  startAt: {
    'ui:widget': 'date',
  },
  endAt: {
    'ui:widget': 'date',
  },
  format: {
    'ui:widget': 'radio',
  },
};

export default function CustomLincExportPayableForm() {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);

  const queueReportMutation = useQueueCustomLincPayableReport();

  const handleSubmit = async (event: IChangeEvent) => {
    const { startAt, endAt, format } = event.formData;
    const fileName = `customlinc_payable_report_${startAt}_${endAt}.${format}`;

    setShowSuccessMessage(false);
    setShowEmptyMessage(false);

    try {
      const text = await queueReportMutation.mutateAsync({ startAt, endAt, format });
      if (format !== 'email') {
        fileDownload(text, fileName);
      } else {
        setShowSuccessMessage(true);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'Empty data set') {
        setShowEmptyMessage(true);
      }
    }
  };

  return (
    <Form
      schema={formSchema}
      uiSchema={uiSchema}
      formData={initialFormData}
      onSubmit={handleSubmit}
      validator={validator}
      showErrorList={false}
    >
      <Button type="submit" variant="contained" disabled={queueReportMutation.isPending}>
        {!queueReportMutation.isPending ? 'Download / Email Report' : 'Loading report...'}
      </Button>

      {queueReportMutation.isError && !showEmptyMessage && (
        <ErrorDisplay message={queueReportMutation.error?.message} />
      )}

      {showEmptyMessage && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No CustomLinc payable items found for the selected date range.
        </Alert>
      )}

      {showSuccessMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Your report is queued for delivery and will be emailed shortly.
        </Alert>
      )}
    </Form>
  );
}
